// WebMCP (https://webmachinelearning.github.io/webmcp/): lets AI agents running in the browser
// use the shop's key actions as tools. The API is experimental; newer Chrome builds expose it on
// document.modelContext, older ones on navigator.modelContext. Without it, nothing happens.

export interface WebMcpTool {
  name: string;
  title?: string;
  description: string;
  inputSchema: Record<string, unknown>;
  annotations?: { readOnlyHint?: boolean };
  execute: (input: Record<string, unknown>) => unknown | Promise<unknown>;
}

interface ModelContext {
  registerTool(tool: WebMcpTool, options?: { signal?: AbortSignal }): Promise<void> | void;
}

function getModelContext(): ModelContext | undefined {
  return (
    (document as unknown as { modelContext?: ModelContext }).modelContext ??
    (navigator as unknown as { modelContext?: ModelContext }).modelContext
  );
}

/** Registers the tools; aborting the returned controller unregisters them. */
export function registerWebMcpTools(tools: WebMcpTool[]): AbortController {
  const controller = new AbortController();
  const modelContext = getModelContext();
  if (!modelContext) return controller;
  for (const tool of tools) {
    Promise.resolve(modelContext.registerTool(tool, { signal: controller.signal })).catch((error) => {
      console.warn(`WebMCP: could not register tool "${tool.name}"`, error);
    });
  }
  return controller;
}

/** Tool results as MCP-style text content, so agents get readable JSON. */
export const toolResult = (data: unknown) => ({
  content: [{ type: "text", text: JSON.stringify(data, null, 2) }],
});
